import { useCallback, useMemo } from 'react'; 
import { useSelector } from 'react-redux';
import {
  getTimeFormat,
  convertTime,
} from 'common/dateFormatConfig';
import { SCHEDULE_DAY_VIEWS, SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import { format } from 'date-fns';
import { adaptedDataHelper } from 'store/schedule/schedulePageOperation';
import { getEffectiveStartTime } from 'helpers/helpers';

const usePrintSchedule = () => {
  const sheets = useSelector((state) => state.schedule.sheets);
  const weekSheets = useSelector((state) => state.schedule.weekSheets);
  const monthSheets = useSelector((state) => state.schedule.monthSheets);
  const scheduleView = useSelector((state) => state.schedule.scheduleView);
  const scheduleType = useSelector((state) => state.schedule.scheduleType);
  const selectedDate = useSelector((state) => state.schedule.selectedDate);
  const equipmentList = useSelector((state) => state.schedule.resources.equipment);
  const workers = useSelector((state) => state.schedule.resources.workers);
  const organizationSettings = useSelector(
    (state) => state.personalProfile.organization?.settings || {}
  );

  const allSheets = useMemo(() => {
    switch (scheduleView) {
      case SCHEDULE_VIEW_TYPES.monthly:
        return adaptedDataHelper(Object.values(monthSheets).flat());
      case SCHEDULE_VIEW_TYPES.weekly:
        return adaptedDataHelper(Object.values(weekSheets).flat());
      default:
        switch (scheduleType) {
          case SCHEDULE_DAY_VIEWS.grid:
            return sheets.filter(sheet => Number.isInteger(sheet.grid.position))
          default:
            return sheets;
        }
    }
  }, [sheets, monthSheets, weekSheets, scheduleView, scheduleType]);

  const { dateFormat, timeFormat } = organizationSettings;

  const printCurrent = useCallback(
    (onWorklogsReady = () => {}) => {
      console.log('allSheets: ', allSheets);

      const worklogs = allSheets.slice()
        .sort((a, b) =>
          a.project?.contractor?.nickname && b.project?.contractor?.nickname
            ? a.project.contractor.nickname.localeCompare(b.project.contractor.nickname)
            : 0
        )
        .map((e) => {
          let startTime;
          if (e.sheet.startTime) {
            const is12Format = getTimeFormat([], timeFormat, dateFormat).is12Format;
            startTime = convertTime(
              getEffectiveStartTime(e.sheet.startTime, e.sheet.startTimeDate),
              is12Format,
            );
          }

          return {
            createdAtDate: e.sheet.createdAtDate,
            start: e.originalStart,
            end: e.originalEnd || e.end || e.start,
            contractorNickname:
              e.project?.contractor?.nickname || e.project?.contractor?.name || '',
            county: e.project?.county,
            routeSection: `${e.project?.route || ''}${
              e.project?.section ? '– ' + e.project.section : ''
            }`,
            jobNumber: e.project?.jobNumber,
            rated: e.project?.rated,
            workers: (e.workers || []).map((worker) => {
              const currentWorker = workers.find((w) => w._id === worker._id);
              const hoursEntry =
                worker.hours?.length || { start: e.start || e.originalStart, end: e.originalEnd }

              const timeFormatConfig = getTimeFormat([], timeFormat, dateFormat);

              return {
                ...worker,
                username: currentWorker?.username || '',
                employeeNum:
                  (currentWorker &&
                    currentWorker.profile &&
                    currentWorker.profile.employeeNum) ||
                  '',
                startTime,
                start:
                  currentWorker && currentWorker.username
                    ? format(
                        hoursEntry.start ? new Date(hoursEntry.start) : new Date(),
                        timeFormatConfig.formatForTimePars
                      )
                    : '',
                end:
                  currentWorker && currentWorker.username
                    ? format(
                        hoursEntry.end ? new Date(hoursEntry.end) : new Date(),
                        timeFormatConfig.formatForTimePars
                      )
                    : '',
              };
            }),
            notes: e.sheet.notes || ' ',
            equipment: e.equipment,
            isCanceled: e.sheet.canceledAt || false,
            canceledAt: e.sheet.canceledAt,
            cancelNote: e.sheet.cancelNote,
            canceledBy:
              e.sheet.canceledBy &&
              (workers.find((w) => w._id === e.sheet.canceledBy) || {}).username,
            id: e.sheet._id,
          };
        })
        .filter((e) => {
          const startThisDay = !(
            selectedDate &&
            new Date(e.start).getTime() < new Date(selectedDate).setHours(0, 0, 0, 0)
          );

          if (SCHEDULE_DAY_VIEWS.grid === scheduleType || startThisDay) {
            return (e.workers && e.workers.length > 0) || e.equipment || e.isCanceled;
          }

          return false;
        });

      onWorklogsReady(worklogs);
    },
    [
      allSheets,
      equipmentList,
      workers,
      selectedDate,
      scheduleType,
      dateFormat,
      timeFormat,
    ]
  );

  return { printCurrent };
}

export default usePrintSchedule;